import { defineDspModule, type DspModuleInstance, type DspProcessBlock } from '../../dsp-module-contract/src/index.js';

export interface AdditivePartial { readonly ratio: number; readonly amplitude: number; readonly phase: number; }
export interface AdditivePartialBankOptions { readonly sampleRate: number; readonly maxBlockSize: number; readonly bank?: number; readonly fundamental?: number; readonly spectralTiltDbPerOctave?: number; readonly level?: number; }
const TAU = Math.PI * 2;
const PARTIALS = 128;
const BANKS = 8;
const bounded = (name: string, value: number, min: number, max: number): number => {
  if (!Number.isFinite(value) || value < min || value > max) throw new RangeError(`${name} must be finite and in [${min}, ${max}]`);
  return value;
};
const makeBank = (bank: number): readonly AdditivePartial[] => Object.freeze(Array.from({ length: PARTIALS }, (_, i) => {
  const n = i + 1;
  let ratio = n;
  let amplitude = 1 / n;
  let phase = 0;
  if (bank === 1) amplitude = n % 2 ? 1 / n : 0;
  else if (bank === 2) amplitude = n % 2 ? 1 / (n * n) : 0;
  else if (bank === 3) { ratio = Math.min(128, n * (1 + 0.0015 * n)); amplitude = Math.exp(-n / 22); phase = (n % 4) * Math.PI / 2 - Math.PI; }
  else if (bank === 4) { ratio = Math.min(128, Math.sqrt(n * n + 0.73 * n)); amplitude = Math.exp(-n / 16); phase = ((n * 0.61803398875) % 2 - 1) * Math.PI; }
  else if (bank === 5) { ratio = Math.min(128, 1 + (n - 1) * 1.41421356237); amplitude = 1 / Math.sqrt(n); phase = n % 2 ? Math.PI / 2 : -Math.PI / 2; }
  else if (bank === 6) { ratio = Math.min(128, Math.pow(n, 1.07)); amplitude = Math.exp(-n / 32); phase = ((n * 0.37) % 2 - 1) * Math.PI; }
  else if (bank === 7) { ratio = Math.min(128, 0.5 + n * 0.997 + 0.08 * Math.sin(n)); amplitude = Math.exp(-n / 48); phase = ((n * n * 0.11) % 2 - 1) * Math.PI; }
  return Object.freeze({ ratio, amplitude, phase });
}));
export const ADDITIVE_BANKS: readonly (readonly AdditivePartial[])[] = Object.freeze(Array.from({ length: BANKS }, (_, i) => makeBank(i)));

/** Deterministic bounded additive oscillator. All block storage is caller-owned. */
export class AdditivePartialBank {
  readonly sampleRate: number;
  readonly maxBlockSize: number;
  private bank: number;
  private fundamental: number;
  private tilt: number;
  private level: number;
  private readonly phases = new Float64Array(PARTIALS);
  private readonly increments = new Float64Array(PARTIALS);
  private readonly gains = new Float64Array(PARTIALS);
  private activeCount = 0;
  constructor(options: AdditivePartialBankOptions) {
    this.sampleRate = bounded('sampleRate', options.sampleRate, 8_000, 192_000);
    this.maxBlockSize = bounded('maxBlockSize', options.maxBlockSize, 1, 4_096);
    if (!Number.isInteger(this.maxBlockSize)) throw new RangeError('maxBlockSize must be an integer');
    this.bank = options.bank ?? 0;
    this.fundamental = options.fundamental ?? 110;
    this.tilt = options.spectralTiltDbPerOctave ?? 0;
    this.level = options.level ?? 0.8;
    this.validateBank(this.bank); bounded('fundamental', this.fundamental, 8, 20_000); bounded('spectralTiltDbPerOctave', this.tilt, -24, 6); bounded('level', this.level, 0, 1);
    this.rebuild(); this.reset();
  }
  private validateBank(value: number): void { if (!Number.isInteger(value) || value < 0 || value >= BANKS) throw new RangeError('bank must be an integer in [0, 7]'); }
  private rebuild(): void {
    const table = ADDITIVE_BANKS[this.bank]!;
    let sum = 0; this.activeCount = 0;
    for (let i = 0; i < PARTIALS; i++) {
      const p = table[i]!; const frequency = this.fundamental * p.ratio;
      const active = frequency < this.sampleRate * 0.49;
      const tiltGain = Math.pow(10, (this.tilt * Math.log2(p.ratio)) / 20);
      const gain = active ? p.amplitude * tiltGain : 0;
      this.gains[i] = gain; this.increments[i] = active ? TAU * frequency / this.sampleRate : 0;
      if (active && gain > 0) this.activeCount = i + 1;
      sum += gain;
    }
    const normalization = sum > 1 ? 1 / sum : 1;
    for (let i = 0; i < PARTIALS; i++) this.gains[i] = this.gains[i]! * normalization * this.level;
  }
  reset(): void { const table = ADDITIVE_BANKS[this.bank]!; for (let i = 0; i < PARTIALS; i++) this.phases[i] = table[i]!.phase; }
  setBank(value: number): void { this.setParameters([['bank', value]]); }
  setFundamental(value: number): void { this.setParameters([['fundamental', value]]); }
  setSpectralTilt(value: number): void { this.setParameters([['spectral-tilt', value]]); }
  setLevel(value: number): void { this.setParameters([['level', value]]); }
  setParameter(id: string, value: number): void { this.setParameters([[id, value]]); }
  setParameters(updates: readonly (readonly [string, number])[]): void {
    let bank = this.bank;
    let fundamental = this.fundamental;
    let tilt = this.tilt;
    let level = this.level;
    for (const [id, value] of updates) {
      if (id === 'bank') { this.validateBank(value); bank = value; }
      else if (id === 'fundamental') fundamental = bounded('fundamental', value, 8, 20_000);
      else if (id === 'spectral-tilt') tilt = bounded('spectralTiltDbPerOctave', value, -24, 6);
      else if (id === 'level') level = bounded('level', value, 0, 1);
      else throw new RangeError(`unknown parameter: ${id}`);
    }
    this.bank = bank;
    this.fundamental = fundamental;
    this.tilt = tilt;
    this.level = level;
    this.rebuild();
  }
  processInto(output: Float32Array, frames = output.length): void {
    if (!Number.isInteger(frames) || frames < 1 || frames > this.maxBlockSize || frames > 4_096) throw new RangeError('frames are outside the declared block');
    if (output.length < frames) throw new RangeError('output buffer is too short');
    for (let frame = 0; frame < frames; frame++) {
      let sum = 0;
      for (let i = 0; i < this.activeCount; i++) {
        sum += Math.sin(this.phases[i]!) * this.gains[i]!;
        let phase = this.phases[i]! + this.increments[i]!;
        if (phase >= TAU) phase -= TAU * Math.floor(phase / TAU);
        this.phases[i] = phase;
      }
      output[frame] = sum;
    }
  }
}
class Instance implements DspModuleInstance {
  private readonly synth: AdditivePartialBank;
  constructor(sampleRate: number, private readonly maxBlockSize: number) { this.synth = new AdditivePartialBank({ sampleRate, maxBlockSize }); }
  reset(): void { this.synth.reset(); }
  setParameter(id: string, value: number): void { this.synth.setParameter(id, value); }
  process(block: DspProcessBlock): void {
    const out = block.outputs.out;
    if (!out) throw new RangeError('out buffer is required');
    const updates: Array<readonly [string, number]> = [];
    for (const [id, control] of Object.entries(block.controls)) if (control.length) updates.push([id, control[0]!]);
    if (updates.length) this.synth.setParameters(updates);
    this.synth.processInto(out, block.frames);
  }
}
const definition = defineDspModule({
  descriptor: { id: 'mir.mod-002', name: 'Additive partial bank', version: '0.1.0', inputs: [], outputs: [{ id: 'out', kind: 'audio', channels: 1 }], parameters: [
    { id: 'bank', defaultValue: 0, minValue: 0, maxValue: 7, unit: 'enum' },
    { id: 'fundamental', defaultValue: 110, minValue: 8, maxValue: 20_000, unit: 'Hz' },
    { id: 'spectral-tilt', defaultValue: 0, minValue: -24, maxValue: 6, unit: 'dB/oct' },
    { id: 'level', defaultValue: 0.8, minValue: 0, maxValue: 1, unit: 'ratio' },
  ] },
  createInstance(context) { return new Instance(context.sampleRate, context.maxBlockSize); },
});
export const mod002 = Object.freeze({ ...definition, banks: ADDITIVE_BANKS });
export default mod002;
